import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { parseBowlingCsv, ParsedBowlerScore } from '../bookie/csvScoreParser';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface Bowler { id: number; name: string; email: string; lane?: number | null; letter?: string | null; handicap?: number; previous_letter?: string | null; }
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const MAX_GAMES = 6;
const SCORE_KEY = (bowlerId: number, game: number) => `${bowlerId}-${game}`;

interface Props {
  squadId: number;
  squadName: string;
  squadDate: string;
  gamesPerMatch: number;
  onClose: () => void;
}

// ─── Status icon ───────────────────────────────────────────────────────────────
const StatusIcon: React.FC<{ status: SaveStatus }> = ({ status }) => {
  if (status === 'saving') return (
    <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
  );
  if (status === 'saved') return <span className="text-green-400 text-sm">✓</span>;
  if (status === 'error') return <span className="text-red-400 text-sm">✕</span>;
  return null;
};

const AdminQuickScoreEntry: React.FC<Props> = ({ squadId, squadName, squadDate, gamesPerMatch, onClose }) => {
  const [bowlers, setBowlers] = useState<Bowler[]>([]);
  const [loadingBowlers, setLoadingBowlers] = useState(true);
  const [savedScores, setSavedScores] = useState<Record<string, number>>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, SaveStatus>>({});
  const [activeGame, setActiveGame] = useState(0);
  const [totalGames, setTotalGames] = useState(Math.max(gamesPerMatch, 3));
  const savingRef = useRef<Record<string, boolean>>({});
  const [letterInputs, setLetterInputs] = useState<Record<number, string>>({});
  const [letterSaveStatus, setLetterSaveStatus] = useState<Record<number, SaveStatus>>({});
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvSummary, setCsvSummary] = useState<{ imported: number; skipped: number; warnings: string[] } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeResult, setFinalizeResult] = useState<'success' | 'error' | null>(null);
  const [unpaidBowlerIds, setUnpaidBowlerIds] = useState<Set<number>>(new Set());

  // ── Lane/letter helpers ───────────────────────────────────────────────────
  const getLetterOptions = (lane: number | null | undefined): string[] => {
    if (lane == null || lane < 1) return [];
    return lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
  };

  const letterToPosition = useCallback((letter: string, lane: number): number => {
    const opts = lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
    const idx = opts.indexOf(letter);
    return idx === -1 ? 0 : idx + 1;
  }, []);

  const formatName = (name: string) => {
    const idx = name.indexOf(' ');
    if (idx === -1) return name;
    return `${name.slice(idx + 1)}, ${name.slice(0, idx)}`;
  };

  const sortBowlersByLane = (list: Bowler[]) =>
    [...list].sort((a, b) => {
      if (a.lane == null && b.lane == null) return a.name.localeCompare(b.name);
      if (a.lane == null) return 1;
      if (b.lane == null) return -1;
      if (a.lane !== b.lane) return a.lane - b.lane;
      if (a.letter == null && b.letter == null) return a.name.localeCompare(b.name);
      if (a.letter == null) return 1;
      if (b.letter == null) return -1;
      return a.letter.localeCompare(b.letter) || a.name.localeCompare(b.name);
    });

  // ── Auto-assign positions for bowlers with lanes but no letter ─────────────
  // Pure computation: returns map of bowlerId → letter to assign.
  const calcAutoAssignments = useCallback((bowlerList: Bowler[]): Record<number, string> => {
    const result: Record<number, string> = {};
    const byLane = new Map<number, Bowler[]>();
    bowlerList.forEach(b => {
      if (b.lane != null && !b.letter) {
        if (!byLane.has(b.lane)) byLane.set(b.lane, []);
        byLane.get(b.lane)!.push(b);
      }
    });
    byLane.forEach((group, lane) => {
      const opts = lane % 2 === 1 ? ['A', 'C', 'E', 'G', 'I'] : ['B', 'D', 'F', 'H', 'J'];
      const taken = new Set(bowlerList.filter(b => b.lane === lane && b.letter).map(b => b.letter!));
      // Sort: bowlers with a previous letter come first, then alphabetical
      const sorted = [...group].sort((a, b) => {
        const aHas = a.previous_letter ? 1 : 0;
        const bHas = b.previous_letter ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        return a.name.localeCompare(b.name);
      });
      const used = new Set(taken);
      const assigned = new Map<number, string>();
      // First pass: honour previous letter from last week (if available and no conflict)
      sorted.forEach(b => {
        if (b.previous_letter && opts.includes(b.previous_letter) && !used.has(b.previous_letter)) {
          assigned.set(b.id, b.previous_letter);
          used.add(b.previous_letter);
        }
      });
      // Second pass: fill remaining slots sequentially
      sorted.forEach(b => {
        if (!assigned.has(b.id)) {
          const avail = opts.find(l => !used.has(l));
          if (avail) { assigned.set(b.id, avail); used.add(avail); }
        }
      });
      assigned.forEach((letter, id) => { result[id] = letter; });
    });
    return result;
  }, []);

  // ── Save letter (position) ────────────────────────────────────────────────
  const saveLetter = useCallback((bowlerId: number, value: string) => {
    setLetterSaveStatus(prev => ({ ...prev, [bowlerId]: 'saving' }));
    axios.put(
      `${API}/admin/quick-scores/squads/${squadId}/bowlers/${bowlerId}/letter`,
      { letter: value || null },
      { headers: authHeader() }
    ).then(() => {
      setLetterSaveStatus(prev => ({ ...prev, [bowlerId]: 'saved' }));
      setBowlers(prev => {
        const updated = prev.map(b => b.id === bowlerId ? { ...b, letter: value || null } : b);
        return sortBowlersByLane(updated);
      });
      setTimeout(() => setLetterSaveStatus(prev => ({ ...prev, [bowlerId]: 'idle' })), 1500);
    }).catch(() => {
      setLetterSaveStatus(prev => ({ ...prev, [bowlerId]: 'error' }));
    });
  }, [squadId]);

  // ── Handle letter change with auto-swap ───────────────────────────────────
  const handleLetterChange = useCallback((bowlerId: number, lane: number | null | undefined, newLetter: string) => {
    if (!lane) return;
    const currentLetter = letterInputs[bowlerId] ?? '';
    if (!newLetter) {
      setLetterInputs(prev => ({ ...prev, [bowlerId]: '' }));
      saveLetter(bowlerId, '');
      return;
    }
    // Detect conflict: another bowler on same lane already has this letter
    const conflict = bowlers.find(b =>
      b.id !== bowlerId && b.lane === lane &&
      (letterInputs[b.id] !== undefined ? letterInputs[b.id] : (b.letter ?? '')) === newLetter
    );
    if (conflict) {
      setLetterInputs(prev => ({
        ...prev,
        [bowlerId]: newLetter,
        [conflict.id]: currentLetter,
      }));
      saveLetter(bowlerId, newLetter);
      saveLetter(conflict.id, currentLetter);
    } else {
      setLetterInputs(prev => ({ ...prev, [bowlerId]: newLetter }));
      saveLetter(bowlerId, newLetter);
    }
  }, [bowlers, letterInputs, saveLetter]);

  // ── Load bowlers + scores ─────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoadingBowlers(true);
    try {
      const [bRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/quick-scores/squads/${squadId}/scoring-bowlers`, { headers: authHeader() }),
        axios.get(`${API}/admin/quick-scores/squads/${squadId}/scores`, { headers: authHeader() }),
      ]);

      const sorted = sortBowlersByLane(bRes.data as Bowler[]);

      // Auto-assign positions for bowlers with lanes but no letter yet
      const autoAssignments = calcAutoAssignments(sorted);
      let finalSorted = sorted;
      if (Object.keys(autoAssignments).length > 0) {
        finalSorted = sortBowlersByLane(
          sorted.map(b => autoAssignments[b.id] != null ? { ...b, letter: autoAssignments[b.id] } : b)
        );
      }
      setBowlers(finalSorted);

      // Initialise letter inputs from loaded data (merged with auto-assignments)
      const initLetters: Record<number, string> = {};
      for (const b of finalSorted) { initLetters[b.id] = b.letter ?? ''; }
      setLetterInputs(initLetters);

      // Persist auto-assignments to backend
      Object.keys(autoAssignments).forEach(idStr => {
        axios.put(
          `${API}/admin/quick-scores/squads/${squadId}/bowlers/${idStr}/letter`,
          { letter: autoAssignments[Number(idStr)] },
          { headers: authHeader() }
        ).catch(() => {});
      });

      // Initialise saved scores and determine totalGames
      const scores: Record<string, number> = {};
      let maxGame = Math.max(gamesPerMatch, 3);
      for (const row of sRes.data as any[]) {
        scores[SCORE_KEY(row.bowler_id, row.game_number)] = row.score;
        if (row.game_number > maxGame) maxGame = row.game_number;
      }
      setSavedScores(scores);
      setTotalGames(Math.min(maxGame, MAX_GAMES));

      // Fetch payment status (informational, non-blocking)
      try {
        const psRes = await axios.get(`${API}/admin/quick-scores/squads/${squadId}/payment-status`, { headers: authHeader() });
        setUnpaidBowlerIds(new Set((psRes.data.unpaidMembers || []).map((m: any) => m.bowlerId as number)));
      } catch { /* non-blocking */ }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoadingBowlers(false);
    }
  }, [squadId, gamesPerMatch, calcAutoAssignments]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Save one score ────────────────────────────────────────────────────────
  const saveScore = useCallback(async (bowlerId: number, gameNumber: number, rawScore: string) => {
    const score = parseInt(rawScore, 10);
    if (isNaN(score) || score < 0 || score > 300) return;

    const key = SCORE_KEY(bowlerId, gameNumber);
    if (savingRef.current[key]) return;
    savingRef.current[key] = true;
    setSaveStatus(prev => ({ ...prev, [key]: 'saving' }));

    try {
      await axios.post(
        `${API}/admin/quick-scores/squads/${squadId}/scores`,
        { bowlerId, gameNumber, score },
        { headers: authHeader() }
      );
      setSavedScores(prev => ({ ...prev, [key]: score }));
      setSaveStatus(prev => ({ ...prev, [key]: 'saved' }));
      setTimeout(() => setSaveStatus(prev => ({ ...prev, [key]: 'idle' })), 1500);
    } catch {
      setSaveStatus(prev => ({ ...prev, [key]: 'error' }));
    } finally {
      savingRef.current[key] = false;
    }
  }, [squadId]);

  const handleInput = (bowlerId: number, value: string) => {
    const key = SCORE_KEY(bowlerId, activeGame);
    setInputValues(prev => ({ ...prev, [key]: value }));
    setSaveStatus(prev => ({ ...prev, [key]: 'idle' }));
  };

  const handleBlur = (bowlerId: number) => {
    const key = SCORE_KEY(bowlerId, activeGame);
    const raw = inputValues[key] ?? '';
    if (raw.trim() === '') return;
    saveScore(bowlerId, activeGame, raw);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, bowlerId: number, listIndex: number) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const key = SCORE_KEY(bowlerId, activeGame);
      const raw = inputValues[key] ?? '';
      if (raw.trim() !== '') saveScore(bowlerId, activeGame, raw);

      if (activeGame === 0) {
        // All-games mode: advance through games then bowlers
        const curGame = 1; // tab moves to next input regardless
        const nextId = `score-${bowlerId}-${curGame + 1 <= totalGames ? curGame + 1 : curGame}`;
        const next = document.getElementById(nextId) ?? document.getElementById(`score-${bowlers[listIndex + 1]?.id}-1`);
        next?.focus();
      } else {
        // Single-game mode: next bowler
        const next = document.getElementById(`score-${bowlers[listIndex + 1]?.id}-${activeGame}`);
        next?.focus();
      }
    }
  };

  // ── CSV import ────────────────────────────────────────────────────────────
  const applyCsvScores = useCallback(async (entries: ParsedBowlerScore[]) => {
    // Build lane→position→bowlerId map from bowlers with both lane and letter set
    const lpMap = new Map<string, number>();
    for (const b of bowlers) {
      if (b.lane != null && b.letter != null) {
        const pos = letterToPosition(b.letter, b.lane);
        if (pos > 0) lpMap.set(`${b.lane}-${pos}`, b.id);
      }
    }

    setCsvImporting(true);
    let imported = 0;
    let skipped = 0;
    const warnings: string[] = [];

    for (const entry of entries) {
      const bowlerId = lpMap.get(`${entry.lane}-${entry.position}`);
      if (bowlerId == null) {
        skipped++;
        warnings.push(`No bowler assigned to lane ${entry.lane} pos ${entry.position}`);
        continue;
      }
      for (let gi = 0; gi < Math.min(entry.games.length, MAX_GAMES); gi++) {
        const score = entry.games[gi];
        const gameNumber = gi + 1;
        if (score == null || score < 0 || score > 300) continue;
        const key = SCORE_KEY(bowlerId, gameNumber);
        // sequential to avoid MySQL deadlocks
        await saveScore(bowlerId, gameNumber, String(score));
        setInputValues(prev => ({ ...prev, [key]: String(score) }));
        imported++;
        await new Promise(r => setTimeout(r, 40));
      }
    }

    setCsvImporting(false);
    setCsvSummary({ imported, skipped, warnings });
  }, [bowlers, saveScore, letterToPosition]);

  const handleCsvFile = useCallback(async (file: File) => {
    const text = await file.text();
    const result = parseBowlingCsv(text);
    if (result.entries.length === 0) {
      alert('No valid score rows found in this CSV.');
      return;
    }
    await applyCsvScores(result.entries);
  }, [applyCsvScores]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleCsvFile(file);
    e.target.value = '';
  };

  // ── Drag-and-drop ─────────────────────────────────────────────────────────
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const onDragLeave = () => setIsDragOver(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) handleCsvFile(file);
  };

  // ── Publish Results ───────────────────────────────────────────────────────
  const publishResults = useCallback(async () => {
    setFinalizing(true);
    setFinalizeResult(null);
    try {
      await axios.post(
        `${API}/admin/quick-scores/squads/${squadId}/finalize`,
        {},
        { headers: authHeader() }
      );
      setFinalizeResult('success');
      setTimeout(() => setFinalizeResult(null), 3000);
    } catch {
      setFinalizeResult('error');
      setTimeout(() => setFinalizeResult(null), 3000);
    } finally {
      setFinalizing(false);
    }
  }, [squadId]);

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs = [0, ...Array.from({ length: totalGames }, (_, i) => i + 1)];

  // ── Search ───────────────────────────────────────────────────────────────
  const matchedBowlerId = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const fmt = (name: string) => { const i = name.indexOf(' '); return i === -1 ? name : `${name.slice(i + 1)}, ${name.slice(0, i)}`; };
    const normalized = (q.includes(' ') && !q.includes(',')) ? q.replace(' ', ', ') : q;
    const match = bowlers.find(b =>
      fmt(b.name).toLowerCase().startsWith(normalized) ||
      b.name.toLowerCase().startsWith(q)
    );
    return match?.id ?? null;
  }, [searchQuery, bowlers]);

  useEffect(() => {
    if (matchedBowlerId == null) return;
    rowRefs.current[matchedBowlerId]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [matchedBowlerId]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: '#111827' }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drag-over overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center pointer-events-none"
          style={{ background: 'rgba(99,102,241,0.18)', border: '3px dashed #6366f1' }}>
          <div className="bg-gray-900 rounded-2xl px-10 py-8 flex flex-col items-center gap-3 shadow-2xl">
            <span className="text-4xl">📄</span>
            <p className="text-white font-bold text-lg">Drop CSV to import scores</p>
          </div>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700 flex-shrink-0"
        style={{ backgroundColor: '#0f1623' }}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">⚡</span>
          <div>
            <h2 className="text-white font-bold text-base leading-tight">Quick Score Entry</h2>
            <p className="text-gray-400 text-xs">{squadName} · {new Date(squadDate).toLocaleDateString()}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Games: read-only badge */}
          <span className="px-2 py-1 rounded bg-gray-700 text-gray-300 text-xs font-medium">
            {totalGames} {totalGames === 1 ? 'game' : 'games'}
          </span>

          {/* CSV import */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
          >
            <span>📄</span> Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFileChange} className="hidden" />

          {/* Publish Results */}
          <button
            onClick={publishResults}
            disabled={finalizing || csvImporting}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors
              ${finalizeResult === 'success' ? 'bg-green-600 text-white' :
                finalizeResult === 'error' ? 'bg-red-600 text-white' :
                'bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 disabled:cursor-not-allowed'}`}
          >
            {finalizing ? (
              <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
            ) : finalizeResult === 'success' ? (
              <>✓ Published!</>
            ) : finalizeResult === 'error' ? (
              <>✕ Failed</>
            ) : (
              <>🏆 Publish Results</>
            )}
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-colors text-lg"
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Game tabs ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-5 py-2 border-b border-gray-700 flex-shrink-0 overflow-x-auto"
        style={{ backgroundColor: '#0f1623' }}>
        {tabs.map(g => (
          <button
            key={g}
            onClick={() => setActiveGame(g)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors
              ${g === activeGame
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'}`}
          >
            {g === 0 ? 'All Games' : `Game ${g}`}
          </button>
        ))}
      </div>
      {/* ── Search bar ───────────────────────────────────────────────────── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-2 flex-shrink-0">
        <div className="relative max-w-xs">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Find bowler…"
            className="w-full bg-gray-700 text-white text-xs placeholder-gray-500 rounded-lg pl-8 pr-7 py-1.5 border border-gray-600 focus:outline-none focus:border-indigo-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-sm leading-none"
            >×</button>
          )}
        </div>
      </div>
      {/* ── Score grid ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loadingBowlers ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : bowlers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-4xl mb-3">🎳</div>
            <p className="text-gray-400 text-sm">No bowlers found. Make sure teams have members and lanes are assigned.</p>
          </div>
        ) : (
          <>
            {/* All-games header */}
            {activeGame === 0 && (
              <div
                className="grid items-center gap-2 px-3 py-1.5 mb-1 text-xs font-semibold text-gray-500 uppercase"
                style={{ gridTemplateColumns: `56px 52px 1fr ${Array(totalGames).fill('80px').join(' ')} 52px 24px` }}
              >
                <span>Lane</span>
                <span>Pos</span>
                <span>Bowler</span>
                {Array.from({ length: totalGames }, (_, i) => (
                  <span key={i} className="text-center">G{i + 1}</span>
                ))}
                <span className="text-center">Series</span>
                <span />
              </div>
            )}

            {/* Single-game header */}
            {activeGame !== 0 && (
              <div
                className="grid items-center gap-2 px-3 py-1.5 mb-1 text-xs font-semibold text-gray-500 uppercase"
                style={{ gridTemplateColumns: '56px 52px 1fr 120px 24px' }}
              >
                <span>Lane</span>
                <span>Pos</span>
                <span>Bowler</span>
                <span className="text-center">Score</span>
                <span />
              </div>
            )}

            {/* Bowler rows */}
            <div className="space-y-1">
              {bowlers.map((bowler, idx) => {
                const letterOpts = getLetterOptions(bowler.lane);
                const hasLane = bowler.lane != null;

                if (activeGame === 0) {
                  // ── All Games row ──
                  const series = Array.from({ length: totalGames }, (_, i) => {
                    const k = SCORE_KEY(bowler.id, i + 1);
                    return savedScores[k] ?? 0;
                  }).reduce((a, b) => a + b, 0);

                  return (
                    <div
                      key={bowler.id}
                      ref={el => { rowRefs.current[bowler.id] = el; }}
                      className={`grid items-center gap-2 px-3 py-2 rounded-xl transition-colors
                        ${bowler.id === matchedBowlerId ? 'bg-gray-800 border border-purple-500 ring-1 ring-purple-500/60' : 'bg-gray-800/60 hover:bg-gray-800'}`}
                      style={{ gridTemplateColumns: `56px 52px 1fr ${Array(totalGames).fill('80px').join(' ')} 52px 24px` }}
                    >
                      {/* Lane badge (read-only) */}
                      <div className="flex items-center justify-center">
                        {hasLane ? (
                          <span className="text-sm font-bold text-indigo-300 bg-indigo-900/40 rounded px-2 py-0.5">
                            {bowler.lane}
                          </span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </div>

                      {/* Letter/Position select */}
                      <select
                        disabled={!hasLane}
                        value={letterInputs[bowler.id] ?? ''}
                        onChange={e => handleLetterChange(bowler.id, bowler.lane, e.target.value)}
                        className={`w-full text-center px-1 py-1 rounded text-xs font-bold bg-gray-700 border focus:outline-none transition-colors
                          ${!hasLane ? 'opacity-40 cursor-not-allowed border-gray-700 text-gray-500' :
                            letterSaveStatus[bowler.id] === 'saved' ? 'border-green-700/50 text-green-300' :
                            letterSaveStatus[bowler.id] === 'error' ? 'border-red-500 text-red-300' :
                            'border-gray-600 text-white'}`}
                      >
                        <option value="">—</option>
                        {letterOpts.map((l, i) => <option key={l} value={l}>Pos {i + 1}</option>)}
                      </select>

                      {/* Name + HDCP */}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-white text-xs font-medium">{formatName(bowler.name)}</span>
                          {bowler.handicap !== undefined && bowler.handicap > 0 && (
                            <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                              +{bowler.handicap}
                            </span>
                          )}
                          {unpaidBowlerIds.has(bowler.id) && (
                            <span className="text-[9px] font-semibold px-1 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">Unpaid</span>
                          )}
                        </div>
                        <div className="text-gray-500 text-[10px]">{bowler.email}</div>
                      </div>

                      {/* Per-game inputs */}
                      {Array.from({ length: totalGames }, (_, i) => {
                        const g = i + 1;
                        const key = SCORE_KEY(bowler.id, g);
                        const status = saveStatus[key] ?? 'idle';
                        const hasSaved = savedScores[key] !== undefined;
                        return (
                          <input
                            key={g}
                            id={`score-${bowler.id}-${g}`}
                            type="number"
                            min={0}
                            max={300}
                            placeholder={hasSaved ? String(savedScores[key]) : '—'}
                            value={inputValues[key] ?? ''}
                            onChange={e => {
                              setInputValues(prev => ({ ...prev, [key]: e.target.value }));
                              setSaveStatus(prev => ({ ...prev, [key]: 'idle' }));
                            }}
                            onBlur={() => {
                              const raw = inputValues[key] ?? '';
                              if (raw.trim() !== '') saveScore(bowler.id, g, raw);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                const raw = inputValues[key] ?? '';
                                if (raw.trim() !== '') saveScore(bowler.id, g, raw);
                                // Move to next game, then next bowler
                                const nextGame = g < totalGames ? g + 1 : 1;
                                const nextBowler = g < totalGames ? bowler.id : bowlers[idx + 1]?.id;
                                const next = document.getElementById(`score-${nextBowler}-${nextGame}`);
                                next?.focus();
                              }
                            }}
                            className={`w-full text-center px-1 py-1 rounded text-xs font-bold
                              bg-gray-700 border focus:outline-none focus:border-indigo-500 transition-colors
                              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                              ${status === 'error' ? 'border-red-500 text-red-300' :
                                hasSaved && status === 'idle' ? 'border-gray-600 text-gray-400' :
                                'border-gray-600 text-white'}`}
                          />
                        );
                      })}

                      {/* Series */}
                      <div className="text-center text-sm font-bold text-white tabular-nums">
                        {series > 0 ? series : '—'}
                      </div>

                      {/* Status icon for last game */}
                      <div className="flex justify-center">
                        <StatusIcon status={saveStatus[SCORE_KEY(bowler.id, totalGames)] ?? 'idle'} />
                      </div>
                    </div>
                  );
                }

                // ── Single Game row ──
                const key = SCORE_KEY(bowler.id, activeGame);
                const status = saveStatus[key] ?? 'idle';
                const hasSaved = savedScores[key] !== undefined;

                return (
                  <div
                    key={bowler.id}
                    ref={el => { rowRefs.current[bowler.id] = el; }}
                    className={`grid items-center gap-2 px-3 py-2 rounded-xl transition-colors
                      ${bowler.id === matchedBowlerId ? 'bg-gray-800 border border-purple-500 ring-1 ring-purple-500/60' : 'bg-gray-800/60 hover:bg-gray-800'}`}
                    style={{ gridTemplateColumns: '56px 52px 1fr 120px 24px' }}
                  >
                    {/* Lane badge (read-only) */}
                    <div className="flex items-center justify-center">
                      {hasLane ? (
                        <span className="text-sm font-bold text-indigo-300 bg-indigo-900/40 rounded px-2 py-0.5">
                          {bowler.lane}
                        </span>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </div>

                    {/* Letter/Position select */}
                    <select
                      disabled={!hasLane}
                      value={letterInputs[bowler.id] ?? ''}
                      onChange={e => handleLetterChange(bowler.id, bowler.lane, e.target.value)}
                      className={`w-full text-center px-1 py-1 rounded text-xs font-bold bg-gray-700 border focus:outline-none transition-colors
                        ${!hasLane ? 'opacity-40 cursor-not-allowed border-gray-700 text-gray-500' :
                          letterSaveStatus[bowler.id] === 'saved' ? 'border-green-700/50 text-green-300' :
                          letterSaveStatus[bowler.id] === 'error' ? 'border-red-500 text-red-300' :
                          'border-gray-600 text-white'}`}
                    >
                      <option value="">—</option>
                      {letterOpts.map((l, i) => <option key={l} value={l}>Pos {i + 1}</option>)}
                    </select>

                    {/* Name + email + HDCP */}
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-white text-sm font-medium">{formatName(bowler.name)}</span>
                        {bowler.handicap !== undefined && bowler.handicap > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            +{bowler.handicap}
                          </span>
                        )}
                        {unpaidBowlerIds.has(bowler.id) && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">Unpaid</span>
                        )}
                      </div>
                      <div className="text-gray-500 text-xs">{bowler.email}</div>
                    </div>

                    {/* Score input */}
                    <div className="relative">
                      <input
                        id={`score-${bowler.id}-${activeGame}`}
                        type="number"
                        min={0}
                        max={300}
                        placeholder={hasSaved ? String(savedScores[key]) : '—'}
                        value={inputValues[key] ?? ''}
                        onChange={e => handleInput(bowler.id, e.target.value)}
                        onBlur={() => handleBlur(bowler.id)}
                        onKeyDown={e => handleKeyDown(e, bowler.id, idx)}
                        className={`w-full text-center px-2 py-1.5 rounded-lg font-bold text-lg
                          bg-gray-700 border focus:outline-none focus:border-indigo-500 transition-colors
                          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                          ${status === 'error' ? 'border-red-500 text-red-300' :
                            hasSaved && status === 'idle' ? 'border-gray-600 text-gray-400' :
                            'border-gray-600 text-white'}`}
                      />
                    </div>

                    <div className="flex justify-center">
                      <StatusIcon status={status} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick stats (single game) */}
            {activeGame !== 0 && bowlers.some(b => savedScores[SCORE_KEY(b.id, activeGame)] !== undefined) && (
              <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-3 gap-3 text-center">
                {(() => {
                  const gameScores = bowlers
                    .map(b => savedScores[SCORE_KEY(b.id, activeGame)])
                    .filter(s => s !== undefined) as number[];
                  const avg = gameScores.length > 0
                    ? Math.round(gameScores.reduce((a, b) => a + b, 0) / gameScores.length)
                    : 0;
                  const high = gameScores.length > 0 ? Math.max(...gameScores) : 0;
                  return (
                    <>
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-xl font-bold text-white">{high}</div>
                        <div className="text-xs text-gray-500">High Game</div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-xl font-bold text-white">{avg}</div>
                        <div className="text-xs text-gray-500">Average</div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-xl font-bold text-white">{gameScores.length}/{bowlers.length}</div>
                        <div className="text-xs text-gray-500">Entered</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── CSV Importing overlay ──────────────────────────────────────────── */}
      {csvImporting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)' }}>
          <div className="bg-gray-800 border border-gray-700 rounded-2xl px-10 py-8 flex flex-col items-center gap-4 shadow-2xl">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white font-semibold text-base">Importing scores…</p>
            <p className="text-gray-400 text-sm text-center">Matching lanes & positions and saving to the database</p>
          </div>
        </div>
      )}

      {/* ── CSV Result Summary ─────────────────────────────────────────────── */}
      {csvSummary && !csvImporting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)' }}>
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 shadow-2xl flex flex-col gap-4 min-w-[300px] max-w-sm w-full mx-4">
            <h3 className="text-white font-bold text-base flex items-center gap-2">
              <span className="text-green-400">✓</span> CSV Import Complete
            </h3>
            <div className="flex gap-8 justify-center py-2">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">{csvSummary.imported}</div>
                <div className="text-xs text-gray-400 mt-0.5">Imported</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-500">{csvSummary.skipped}</div>
                <div className="text-xs text-gray-400 mt-0.5">Skipped</div>
              </div>
            </div>
            {csvSummary.warnings.length > 0 && (
              <div className="rounded-lg p-3 space-y-1" style={{ background: 'rgba(161,130,0,0.12)', border: '1px solid rgba(202,138,4,0.35)' }}>
                {csvSummary.warnings.slice(0, 5).map((w, i) => (
                  <p key={i} className="text-yellow-300 text-xs leading-snug">{w}</p>
                ))}
                {csvSummary.warnings.length > 5 && (
                  <p className="text-yellow-500 text-xs">…and {csvSummary.warnings.length - 5} more</p>
                )}
              </div>
            )}
            <p className="text-gray-500 text-xs text-center">
              Skipped rows had no matching bowler assigned to that lane &amp; position.
            </p>
            <button
              onClick={() => setCsvSummary(null)}
              className="mt-1 px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white text-sm font-medium rounded-xl transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuickScoreEntry;
